// 中心知识库页面的请求兜底与逐文件上传逻辑（从组件中抽出以便单测）

/**
 * 给 Promise 加超时：request.js 的响应拦截器对 HTTP 错误返回永不 settle 的 Promise，
 * 调用处用它兜底，保证 loading 等状态一定能结束。
 */
export function withTimeout(promise, ms, message = "请求超时") {
  let timer;
  const timeout = new Promise((_, reject) => {
    timer = setTimeout(() => reject(new Error(message)), ms);
  });
  return Promise.race([promise, timeout]).finally(() => clearTimeout(timer));
}

// HTTP 状态码对应的上传失败说明
function httpErrorText(status) {
  if (status === 413) return "HTTP 413：文件超过服务器允许的上传大小";
  if (status === 504) return "HTTP 504：服务器处理超时";
  if (status === 401) return "HTTP 401：登录状态已失效";
  return `HTTP ${status}：上传失败`;
}

// 把单个文件的上传响应（后端 BaseReturn）解析成 {ok, error}
function parseUploadResponse(resp) {
  if (!resp || typeof resp !== "object") {
    return { ok: false, error: "服务器返回异常" };
  }
  if (resp.errcode !== 0) {
    return { ok: false, error: resp.errmsg || "上传失败" };
  }
  const item = Array.isArray(resp.data) ? resp.data[0] : null;
  if (!item) {
    return { ok: false, error: "服务器返回异常" };
  }
  return item.ok ? { ok: true, error: "" } : { ok: false, error: item.error || "上传失败" };
}

/**
 * 逐个文件顺序上传：每个请求只带一个文件，单个失败不影响后续。
 * 直接修改 file.status（uploading/success/fail）与 file.percentage，el-upload 列表据此显示每个文件的进度与结果；
 * status 已为 success 的文件跳过（失败后重新提交只传未成功的）。
 * @param {Array} files el-upload 的文件列表项
 * @param {Function} uploadOne (file, onProgress) => Promise<后端响应体>，失败时 reject
 * @returns {Promise<Array<{name, ok, error}>>} 本次实际上传的文件结果
 */
export async function uploadFilesSequentially(files, uploadOne) {
  const results = [];
  for (const file of files) {
    if (file.status === "success") continue;
    file.status = "uploading";
    file.percentage = 0;
    let result;
    try {
      const resp = await uploadOne(file, (event) => {
        if (event && event.total) {
          file.percentage = Math.round((event.loaded / event.total) * 100);
        }
      });
      result = parseUploadResponse(resp);
    } catch (error) {
      const status = error && error.response && error.response.status;
      result = {
        ok: false,
        error: status ? httpErrorText(status) : (error && error.message) || "网络连接异常",
      };
    }
    file.status = result.ok ? "success" : "fail";
    if (result.ok) file.percentage = 100;
    results.push({ name: file.name, ok: result.ok, error: result.error });
  }
  return results;
}

/** ElMessageBox 的取消（cancel）与关闭（close / Esc）都视为用户放弃，不算错误 */
export function isDialogDismiss(error) {
  return error === "cancel" || error === "close";
}

/**
 * 取错误提示文本；request.js 拦截器对业务错误（errcode=-1）已弹过提示并 reject {url, data}，
 * 这类返回 null 表示不要再提示，避免弹出「[object Object]」。
 */
export function errorText(error) {
  if (error && typeof error === "object" && "data" in error && "url" in error) {
    return null;
  }
  if (error && error.message) return error.message;
  return String(error);
}

/**
 * 交给浏览器原生下载：点击后立即出现在浏览器下载栏并显示进度（依赖后端 Content-Length），
 * 不在内存里攒 Blob。同源请求自动带登录 cookie；后端出错返回非 2xx，浏览器显示下载失败。
 */
export function triggerBrowserDownload(url, filename) {
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  link.style.display = "none";
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

/** 改名输入去掉末尾与原后缀相同的部分（忽略大小写），避免存成「新报告.pdf.pdf」；只剩后缀时原样交给后端校验 */
export function stripDuplicateExt(name, ext) {
  const trimmed = (name || "").trim();
  if (ext && trimmed.length > ext.length && trimmed.toLowerCase().endsWith(ext.toLowerCase())) {
    return trimmed.slice(0, -ext.length).trim();
  }
  return trimmed;
}

/** 上传白名单。与后端 knowledgeAllowedExts 一一对应，加格式要两边同时改 */
export const KNOWLEDGE_ALLOWED_EXTS = [
  "doc",
  "docx",
  "txt",
  "pdf",
  "md",
  "jpg",
  "jpeg",
  "m4a",
  "mp4",
];

export const KNOWLEDGE_MAX_SIZE = 200 * 1024 * 1024;

/**
 * 图片单独一档 10MB：超过这个尺寸的图在服务端必然 OCR 失败（请求体 base64 膨胀后超上限），
 * 只会反复落 failed。把闸门提到上传时，才能把「你的图搜不到」这个静默后果换成一句明确的报错。
 */
export const KNOWLEDGE_MAX_IMAGE_SIZE = 10 * 1024 * 1024;

const KNOWLEDGE_IMAGE_EXTS = ["jpg", "jpeg"];

function knowledgeExtOf(name) {
  const i = (name || "").lastIndexOf(".");
  return i > -1 ? name.slice(i + 1).toLowerCase() : "";
}

/** 该扩展名允许的体积上限（字节） */
export function knowledgeMaxSizeFor(ext) {
  return KNOWLEDGE_IMAGE_EXTS.includes(ext) ? KNOWLEDGE_MAX_IMAGE_SIZE : KNOWLEDGE_MAX_SIZE;
}

/**
 * 上传前校验，通过返回 null、否则返回给用户看的错误文案。
 * 抽出组件是为了能单测——它原先内联在 KnowledgeBase.vue 里，和 ElMessage 绑在一起没法测。
 * 服务端有同一套闸门，这里只是提前报错，不是唯一防线。
 */
export function knowledgeFileError(file) {
  const ext = knowledgeExtOf(file && file.name);
  if (!KNOWLEDGE_ALLOWED_EXTS.includes(ext)) {
    return `不支持的文件格式，仅支持 ${KNOWLEDGE_ALLOWED_EXTS.join("、")}`;
  }
  const max = knowledgeMaxSizeFor(ext);
  if ((file.size || 0) > max) {
    return `文件大小不能超过 ${Math.round(max / 1024 / 1024)}MB`;
  }
  return null;
}

/**
 * 改名/删除按钮只看后端逐行回填的 can_manage，不看 localStorage 里的身份：
 * 那份身份只在登录时写，新授予的负责人不重新登录就会看不到按钮。服务端才是真闸门。
 */
export function canManageRow(row) {
  return (row && row.can_manage) === true;
}

/** 列表顶部的分类 tab；value 即后端 category 参数，空串 = 不筛 */
export const KNOWLEDGE_CATEGORY_TABS = [
  { label: "全部", value: "" },
  { label: "文档", value: "doc" },
  { label: "图片", value: "image" },
  { label: "音视频", value: "media" },
];

/**
 * 把一段纯文本按关键词（字面量、忽略大小写、去首尾空白）切成 [{text, hit}]，
 * 供模板用 {{ }} 渲染、命中段包 <mark> —— 不拼 HTML，所以不需要转义。
 * 空关键词或无命中时整段一个非命中片段；空文本返回空数组。
 */
export function splitByKeyword(text, keyword) {
  const raw = String(text ?? "");
  if (!raw) return [];
  const kw = String(keyword ?? "").trim();
  if (!kw) return [{ text: raw, hit: false }];
  const re = new RegExp(`(${kw.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")})`, "gi");
  // 带捕获组的 split：奇数下标是命中段；丢掉相邻命中之间产生的空串
  return raw
    .split(re)
    .map((part, i) => ({ text: part, hit: i % 2 === 1 }))
    .filter((seg) => seg.text !== "");
}

/**
 * 点片段定位时传给 preview-info 的查询参数。
 * hit_offset 是后端算好的 0 基 rune 偏移，-1 表示没命中正文（仅文件名命中 / 无正文）。
 * 前端只原样回传，绝不做偏移算术——JS 的 UTF-16 口径一掺进来，emoji 和生僻字处就会错位。
 */
export function previewQuery(row, keyword) {
  const offset = row && typeof row.hit_offset === "number" ? row.hit_offset : -1;
  if (offset < 0) return {};
  return keyword ? { offset, keyword } : { offset };
}

/**
 * 文本预览的降级提示；返回空串表示正常渲染文本窗口。
 *
 * total === 0 也走降级：状态是 ok 但正文被存成空串时，渲染「第 1 - 0 字 / 共 0 字」
 * 加一个空框比直接说「没有正文」更让人困惑。
 */
export function previewTextTip(info) {
  const status = info && info.extract_status;
  if (status === "failed") return "正文抽取失败，可下载原文件查看";
  // 状态要先判完再看 total：pending / processing 的 total 本来就是 0，
  // 先看 total 会把「还在抽」说成「没有文字」—— 一个是过程，一个是结论
  if (status !== "ok" && status !== "empty") return "正文尚未抽取完成，请稍后再试";
  if (status === "empty" || !(info && info.total > 0)) return "文件中未识别到文字内容";
  return "";
}

/**
 * 当前文本窗口的末尾位置（rune 偏移）。
 *
 * 直接取后端给的 next_offset（没有下一窗就是 total），不拿 before/hit/after 的 .length
 * 去算 —— 那是 UTF-16 码元口径，emoji 和生僻字处会和后端的 rune 口径对不上。
 */
export function previewWindowEnd(info) {
  const next = info && info.next_offset;
  return next === null || next === undefined ? info && info.total : next;
}

/**
 * preview-info 的响应判成「渲染什么 / 报什么错」。
 *
 * 入参是 request.js 响应拦截器交出来的东西 —— 注意那已经是**信封**
 * （`{errcode, errmsg, data}`），不是 axios 的 response，所以这里只能剥一层。
 * 组件里曾经写成 `const body = res.data` 再去看 `body.errcode`：载荷上没有 errcode，
 * `undefined !== 0` 恒真，于是每份文档都判失败，而且连后端的 errmsg 都取不到，
 * 屏幕上只剩兜底的「预览失败」四个字 —— 后端一路 200、日志干净，从服务端看不出任何异常。
 * 全仓其余五十多处都按 `res.errcode` 读，那一处是唯一的例外。
 *
 * @returns {{ok: boolean, info: Object, error: string}} ok 为假时 error 必有文案，
 *   info 恒为对象（调用方会直接往里取字段，不能交出 undefined）
 */
export function previewInfoResult(resp) {
  if (!resp || resp.errcode !== 0) {
    return { ok: false, info: {}, error: (resp && resp.errmsg) || "预览失败" };
  }
  return { ok: true, info: resp.data || {}, error: "" };
}

/**
 * pdf.js 阅读器启动前要改的选项（外层页面在 webviewerloaded 事件里调用，传入 PDFViewerApplicationOptions）。
 * - disableAutoFetch / disableStream：只按 Range 拉看到的页。不关的话显示完首屏还会在后台把整份文件拉完，
 *   200MB 的书每打开一次就白耗一份服务器出口带宽
 * - disablePreferences：否则阅读器自己的偏好会把这里设的值覆盖回默认
 * - enableScripting：PDF 内嵌脚本没有用处，关掉也就不用加载 sandbox
 * - defaultUrl：不带 ?file= 时别去加载官方包里的示例文件（已删）
 * - workerSrc：public/pdfjs-4.10.38 里把 .mjs 改名成了 .js（生产 nginx 不认 .mjs 的类型），见那里的 README
 * - localeProperties：界面固定中文。默认跟浏览器语言走，英文系统的电脑会看到英文界面；只带了 zh-CN / en-US 两个语言包
 * - isEvalSupported / externalLinkTarget：上传的 PDF 由与主站同源的 pdf.js 解析，能碰到主站的 localStorage 与 cookie。
 *   关掉字体解析里的 eval（CVE-2024-4367 那一类漏洞的入口）；外链改新标签页（2 = LinkTarget.BLANK），
 *   嵌入时默认是 TOP，点 PDF 里的链接会把整个业务页导航走
 */
export function configurePdfViewer(options) {
  options.set("disablePreferences", true);
  options.set("disableAutoFetch", true);
  options.set("disableStream", true);
  options.set("enableScripting", false);
  options.set("defaultUrl", "");
  options.set("workerSrc", "../build/pdf.worker.js");
  options.set("localeProperties", { lang: "zh-CN" });
  options.set("isEvalSupported", false);
  options.set("externalLinkTarget", 2);
}

/**
 * 在 pdf.js 一页的文字层里就地标出检索词，返回标出的个数。
 *
 * 不用 pdf.js 自带的查找（#search）：它从第 1 页起逐页抽全书文字，默克手册（5392 页）
 * 实测 92 秒才轮到第 3000 页，还几乎把整份文件拉了一遍，按需加载就白做了。
 * 这里只处理已渲染出来的页，由外层在每页 textlayerrendered 时调用。
 *
 * 命中段包成 pdf.js 自己高亮用的 <span class="highlight appended">，沿用它的样式与定位；
 * 已在 highlight 里的文字跳过，重复调用不会套娃。跨文本片段的命中（一个词被拆在两个 span）标不出来。
 */
export function highlightTextLayer(root, keyword) {
  if (!root || !String(keyword ?? "").trim()) return 0;
  const doc = root.ownerDocument;
  const walker = doc.createTreeWalker(root, 4 /* NodeFilter.SHOW_TEXT */);
  const nodes = [];
  for (let n = walker.nextNode(); n; n = walker.nextNode()) {
    if (!(n.parentElement && n.parentElement.closest(".highlight"))) nodes.push(n);
  }
  let count = 0;
  for (const node of nodes) {
    const segs = splitByKeyword(node.nodeValue, keyword);
    if (!segs.some((s) => s.hit)) continue;
    const frag = doc.createDocumentFragment();
    for (const seg of segs) {
      if (seg.hit) {
        const span = doc.createElement("span");
        span.className = "highlight appended";
        span.textContent = seg.text;
        frag.appendChild(span);
        count++;
      } else {
        frag.appendChild(doc.createTextNode(seg.text));
      }
    }
    node.parentNode.replaceChild(frag, node);
  }
  return count;
}

/**
 * 给 pdf.js 阅读器挂上检索词高亮：每页文字层渲染时就地标出（翻到哪页标哪页），
 * 并在跳到的那一页把第一处滚到阅读器视野中间，只滚一次，之后用户自己翻页时不再抢滚动条。
 * 关键词为空时不挂，返回 false。app 是 iframe 里的 PDFViewerApplication，须在 initializedPromise 之后调用。
 */
export function attachPdfKeywordHighlight(app, keyword, targetPage) {
  if (!app || !String(keyword ?? "").trim()) return false;
  let scrolled = false;
  app.eventBus.on("textlayerrendered", ({ source, pageNumber }) => {
    const div = source && source.textLayer && source.textLayer.div;
    const count = highlightTextLayer(div, keyword);
    if (scrolled || count === 0 || pageNumber !== targetPage) return;
    scrolled = true;
    // 只滚阅读器自己的容器：原生 scrollIntoView 会连带滚动外层弹窗（.el-overlay-dialog 可滚动）
    const container = app.pdfViewer && app.pdfViewer.container;
    const hit = div.querySelector(".highlight");
    if (!container || !hit) return;
    const r = hit.getBoundingClientRect();
    const cr = container.getBoundingClientRect();
    container.scrollTop += r.top + r.height / 2 - cr.top - container.clientHeight / 2;
  });
  return true;
}
